import React, { FC, useContext, useEffect, useState } from 'react';
import { Box, Checkbox, FormControlLabel, FormGroup, Radio, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LocalTranslationContext } from '../../hooks/useLocalTranslation';

interface Props {
  question: any;
  step: number;
  onChange: (
    qid: string,
    val: string | string[],
    i: number,
    p: boolean,
    disableNext?: boolean
  ) => Promise<void>;
  value?: string;
  selectedLanguage: string;
}

const CustomQuestionMultiple: FC<Props> = ({
  question,
  step,
  onChange,
  value,
  selectedLanguage,
}) => {
  const [checkedOptions, setCheckedOptions] = useState<number[]>([]);
  const [customValue, setCustomValue] = useState<string>();

  const localContext = useContext(LocalTranslationContext);
  const { t: globalT } = useTranslation('customSurvey');
  const t = localContext ? localContext.t : globalT;

  const currentLanguage = selectedLanguage ?? 'de-DE';

  function getTranslatedOption(idx: number) {
    return question?.questionI18n?.options?.[currentLanguage]?.[idx] ?? question.options[idx].label;
  }

  useEffect(() => {
    const values = value?.split(',') ?? [];
    const labels = question.options.map((o: { label: string }) => o.label);
    const newCheckedOptions = [];
    for (const value of values) {
      newCheckedOptions.push(labels.indexOf(value));
    }
    setCheckedOptions(newCheckedOptions);
  }, []);

  useEffect(() => {
    optionClick({ target: { value: customValue } }, null);
  }, [customValue]);

  const optionClick = async (e: any, value: string | number | null) => {
    const index = Number(value);

    setCheckedOptions((prev) => {
      if (value !== null) {
        if (prev.includes(index)) {
          prev = prev.filter((x) => x !== index);
        } else {
          prev.push(index);
        }
      }
      const values = prev.map((idx) => (idx === -1 ? customValue : question.options[idx].label));

      let disableNext = !question.optional && prev.length === 0;
      if (prev.includes(-1) && (customValue?.length === 0 || customValue === undefined)) {
        disableNext = true;
      }
      if (prev.length > 0) {
        onChange(question.questionId || '', values, step, false, disableNext);
      } else {
        onChange(question.questionId || '', values, step, false, disableNext);
      }
      return prev;
    });
  };

  const updateCustom = (e: any) => {
    if (e.target && e.target.value !== null) {
      setCustomValue(e.target.value);
    }
  };

  return (
    <Box className="survey-custom-question survey-custom-question--multiple">
      <FormGroup className="survey-answer-options survey-answer-options--multiple">
        {question.options.map((option: any, index: number) => (
          <FormControlLabel
            className="survey-answer-option"
            key={question.questionId + '|' + index}
            control={<Checkbox checked={checkedOptions.includes(index)} />}
            label={getTranslatedOption(index)}
            value={option.label}
            onClick={(e) => optionClick(e, index)}
          />
        ))}

        {question.allowCustom && (
          <FormControlLabel
            className="survey-answer-option survey-answer-option--custom"
            key={question.questionId + '|' + -1}
            control={<Checkbox checked={checkedOptions.includes(-1)} />}
            label={
              <Box
                className="survey-custom-answer"
                sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}
              >
                <span className="survey-custom-answer-label">{t('otheroption')}</span>

                <TextField
                  className="survey-custom-answer-input"
                  disabled={!checkedOptions.includes(-1)}
                  value={customValue}
                  inputProps={{ maxLength: 40 }}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => updateCustom(e)}
                />
              </Box>
            }
            value={'others'}
            onClick={(e) => optionClick(e, '-1')}
          />
        )}
      </FormGroup>
    </Box>
  );
};

export default CustomQuestionMultiple;
